import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSeo } from "@/hooks/usePageSeo";

const PrivacyPolicyPage = () => {
  const { language, url, onLanguageChange } = usePageSeo();

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Privacy Policy - ROC Worldwide Agency LLC</title>
        <meta name="description" content="Privacy Policy for ROC Worldwide Agency LLC. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy - Luis ROC" />
        <meta property="og:description" content="Privacy Policy for ROC Worldwide Agency LLC and SEO consulting services." />
        <meta property="og:url" content={url} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="text-center">
                <h1 className="text-3xl lg:text-4xl font-bold mb-4 heading-gradient">
                  Privacy Policy
                </h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <Card className="card-elegant">
                <CardContent className="p-8 space-y-6">
                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">1. Information We Collect</h2>
                    
                    <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      We collect information you provide directly to us, such as when you:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                      <li>Fill out our contact forms or request consultations</li>
                      <li>Subscribe to our newsletter or marketing communications</li>
                      <li>Engage our services through contracts or agreements</li>
                      <li>Communicate with us via email, phone, or other channels</li>
                      <li>Use our website tools and calculators</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3">Business Information</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      For our consulting services, we may collect business-related information including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                      <li>Company name, industry, and business description</li>
                      <li>Website URLs and digital marketing account information</li>
                      <li>Marketing goals, budget information, and business objectives</li>
                      <li>Competitor information and market analysis data</li>
                      <li>Performance metrics and analytics data</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3">Automatically Collected Information</h3>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>IP address, browser type, and device information</li>
                      <li>Pages visited, time spent on site, and navigation patterns</li>
                      <li>Referral sources and search terms used to find our site</li>
                      <li>Cookies and similar tracking technologies data</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">2. How We Use Your Information</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      We use the information we collect to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Provide, maintain, and improve our consulting services</li>
                      <li>Respond to your inquiries and provide customer support</li>
                      <li>Develop customized digital marketing strategies and recommendations</li>
                      <li>Send you service-related communications and updates</li>
                      <li>Process payments and manage billing</li>
                      <li>Analyze website usage and improve user experience</li>
                      <li>Comply with legal obligations and protect our rights</li>
                      <li>Send marketing communications (with your consent)</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">3. Information Sharing and Disclosure</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website, conducting our business, or serving our clients</li>
                      <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government request</li>
                      <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
                      <li><strong>Protection of Rights:</strong> We may disclose information to protect our rights, property, or safety, or that of our clients or others</li>
                      <li><strong>With Your Consent:</strong> We may share information with your explicit consent for specific purposes</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">4. Data Security</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We implement appropriate technical and organizational security measures to protect your personal information against 
                      unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, 
                      access controls, and regular security assessments. However, no method of transmission over the internet or electronic 
                      storage is 100% secure, and we cannot guarantee absolute security.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">5. Data Retention</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We retain personal information for as long as necessary to provide our services, comply with legal obligations, 
                      resolve disputes, and enforce our agreements. For clients, we typically retain information for the duration of 
                      our business relationship plus seven (7) years for tax and legal compliance purposes. Marketing communications 
                      data is retained until you opt out or request deletion.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">6. Your Rights and Choices</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Depending on your location, you may have the following rights regarding your personal information:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                      <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                      <li><strong>Portability:</strong> Request transfer of your information to another service provider</li>
                      <li><strong>Objection:</strong> Object to certain processing activities</li>
                      <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                      <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">7. Cookies and Tracking Technologies</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Our website uses cookies and similar technologies to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Remember your preferences and settings</li>
                      <li>Analyze website traffic and user behavior</li>
                      <li>Provide personalized content and recommendations</li>
                      <li>Enable certain website features and functionality</li>
                      <li>Measure the effectiveness of our marketing campaigns</li>
                    </ul>
                    <p className="text-muted-foreground leading-relaxed mt-4">
                      You can control cookies through your browser settings, but disabling cookies may affect website functionality.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">8. Third-Party Services</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Our website and services may integrate with third-party platforms such as Google Analytics, social media platforms, 
                      and marketing automation tools. These third parties have their own privacy policies, and we encourage you to review 
                      them. We are not responsible for the privacy practices of third-party websites or services.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">9. International Data Transfers</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Our services are operated from the United States. If you are located outside the United States, please be aware 
                      that information we collect may be transferred to and processed in the United States, which may have different 
                      data protection laws than your country of residence.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">10. Children's Privacy</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Our services are not intended for children under 18 years of age. We do not knowingly collect personal information 
                      from children under 18. If we learn that we have collected personal information from a child under 18, we will 
                      take steps to delete such information from our files as soon as possible.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">11. Changes to This Privacy Policy</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                      Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy 
                      Policy periodically for any changes.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">12. Contact Us</h2>
                    <div className="text-muted-foreground leading-relaxed">
                      <p className="mb-4">
                        If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                      </p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <p className="mb-2">
                          <strong>ROC Worldwide Agency LLC</strong>
                        </p>
                        <p>9002 Six Pines Dr, Suite 277</p>
                        <p>Shenandoah, TX 77380, United States</p>
                        <p>Phone: (832) 521-5050</p>
                        <p>Email: luisroc@luisroc.com</p>
                      </div>
                    </div>
                  </section>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;